import React, { useEffect, useState } from 'react';
import { Activity, AlertCircle, RefreshCw, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, fmtDate } from '../../../lib/adminApi';

const EVENT_COLOR = {
  login: 'bg-emerald-50 text-emerald-700',
  logout: 'bg-slate-100 text-slate-600',
  admin_action: 'bg-violet-50 text-violet-700',
  error: 'bg-rose-50 text-rose-700',
  review_initiator_sync: 'bg-cyan-50 text-cyan-700',
};

export const ActivityHubTab = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [eventType, setEventType] = useState('');
  const [cleaning, setCleaning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        adminApi.activityLogs(filter, eventType),
        adminApi.activitySummary(),
      ]);
      setLogs(Array.isArray(l) ? l : (l?.logs || []));
      setSummary(s);
    } catch (err) {
      toast.error(err.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleCleanup = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Permanently delete activity logs older than 90 days?')) return;
    setCleaning(true);
    try {
      const r = await adminApi.cleanupLogs();
      toast.success(`Removed ${r.deleted ?? 0} log entries`);
      load();
    } catch (err) {
      toast.error(err.message || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div data-testid="tab-activity" className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Last 24h', summary.total_24h],
            ['Last 7d', summary.total_7d],
            ['Last 30d', summary.total_30d],
            ['Logins (24h)', summary.event_counts_24h?.login || 0],
          ].map(([label, val]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs text-slate-500">{label}</div>
              <div className="text-2xl font-bold text-slate-900">{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Email or details…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Event type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-cyan-400 bg-white"
            >
              <option value="">All</option>
              <option value="login">login</option>
              <option value="logout">logout</option>
              <option value="admin_action">admin_action</option>
              <option value="error">error</option>
              <option value="review_initiator_sync">review_initiator_sync</option>
            </select>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Apply
          </button>
          <button onClick={handleCleanup} disabled={cleaning} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" /> {cleaning ? 'Cleaning…' : 'Cleanup 90d+'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="text-sm text-slate-500 p-6">Loading…</div>}
        {!loading && logs.length === 0 && (
          <div className="text-sm text-slate-500 p-6 text-center">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No activity logs matched your filters.
          </div>
        )}
        {!loading && logs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">When</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">User</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">Event</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.slice(0, 200).map((row) => (
                <tr key={row.id || `${row.timestamp}-${row.user_email}-${row.event_type}`} className="border-b last:border-b-0 border-slate-100">
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(row.timestamp)}</td>
                  <td className="px-4 py-2 text-xs text-slate-700">{row.user_email || row.user_id || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${EVENT_COLOR[row.event_type] || 'bg-slate-100 text-slate-700'}`}>
                      <Activity className="w-3 h-3" /> {row.event_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-md truncate" title={row.details}>{row.details || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
